import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Share2, Download, Copy, X, Check,
  MessageCircle, Twitter, Instagram, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_COLORS = {
  energy: '#F59E0B', sleep: '#6366F1', immunity: '#10B981', stress: '#EC4899',
  weight: '#3B82F6', heart: '#EF4444', digestion: '#14B8A6', joints: '#F97316',
  hair_skin: '#8B5CF6', brain_focus: '#6366F1', mood: '#FBBF24', vision: '#0EA5E9',
};

function ShareCardPreview({ data }) {
  if (!data) return null;

  const scores = Object.entries(data.scores || {}).slice(0, 8);
  const maxScore = 100;

  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] rounded-2xl p-6 text-white w-full max-w-sm mx-auto" data-testid="share-card-preview">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-white font-bold text-sm">V</span>
          </div>
          <span className="text-sm font-semibold text-white/90">vitala.me</span>
        </div>
        <span className="text-xs text-white/50">Health Profile</span>
      </div>

      {/* Name */}
      <h2 className="text-lg font-bold mb-4">{data.first_name}'s Health Scores</h2>

      {/* Score Bars */}
      <div className="space-y-2 mb-5">
        {scores.map(([cat, score]) => {
          const color = SCORE_COLORS[cat] || '#6B7280';
          return (
            <div key={cat} className="flex items-center gap-2">
              <span className="text-[10px] text-white/70 w-20 truncate capitalize">{cat.replace('_', ' ')}</span>
              <div className="flex-1 h-2.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${score}%`, backgroundColor: color }}
                />
              </div>
              <span className="text-[10px] font-semibold text-white/80 w-8 text-right">{score}%</span>
            </div>
          );
        })}
      </div>

      {/* Top Supplements */}
      {data.top_supplements?.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-white/50 mb-2 uppercase tracking-wider">Top Recommended</p>
          <div className="flex flex-wrap gap-1.5">
            {data.top_supplements.map((s, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-white/10 text-[10px] font-medium text-white/80">
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="pt-3 border-t border-white/10 text-center">
        <p className="text-xs text-white/60">Discover your personalized plan at</p>
        <p className="text-sm font-semibold text-primary">vitala.me</p>
      </div>
    </div>
  );
}

export function ShareResultsButton({ assessmentId, getAuthHeader }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/share/card/${assessmentId}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load share card');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const link = data?.referral_link || `https://vitala.me/?ref=${assessmentId.slice(0, 8)}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const link = data?.referral_link || 'https://vitala.me';
    const text = `Check out my health profile on Vitala! ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareTwitter = () => {
    const link = data?.referral_link || 'https://vitala.me';
    const text = `Just got my personalized supplement plan from @VitalaHealth! Discover yours:`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`, '_blank');
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpen}
        className="gap-2"
        data-testid="share-results-btn"
      >
        <Share2 className="w-4 h-4" />
        Share Results
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="share-modal"
            >
              <div className="p-5 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-[#1A1A1A]">Share Your Results</h3>
                <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-[#1A1A1A]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    <ShareCardPreview data={data} />

                    <div className="mt-5 space-y-2">
                      <p className="text-xs text-muted-foreground mb-2 text-center">Share via</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={handleShareWhatsApp}
                          className="flex items-center justify-center gap-2 p-3 rounded-xl bg-green-50 text-green-700 hover:bg-green-100 transition-colors text-sm font-medium"
                          data-testid="share-whatsapp"
                        >
                          <MessageCircle className="w-4 h-4" /> WhatsApp
                        </button>
                        <button
                          onClick={handleShareTwitter}
                          className="flex items-center justify-center gap-2 p-3 rounded-xl bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium"
                          data-testid="share-twitter"
                        >
                          <Twitter className="w-4 h-4" /> X / Twitter
                        </button>
                      </div>
                      <button
                        onClick={handleCopyLink}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors text-sm font-medium"
                        data-testid="share-copy-link"
                      >
                        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy Referral Link'}
                      </button>
                    </div>
                    
                    {data?.referral_link && (
                      <p className="text-[10px] text-muted-foreground text-center mt-3">
                        Your referral link: {data.referral_link}
                      </p>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
